/** Строка данных как литерал формулы, а не фрагмент исполняемого выражения.
 *  Одинарные кавычки сохраняют прежний формат подготовленных формул. */
const stringContent = (value: string, quote: string): string => {
    const content = JSON.stringify(value)
        .slice(1, -1)
        .replace(/[{}()]/g, (char) => '\\u' + char.charCodeAt(0).toString(16).padStart(4, '0'));
    return quote === "'" ? content.replace(/'/g, "\\'") : content;
};

export const formulaStringLiteral = (value: string): string => "'" + stringContent(value, "'") + "'";

/** Самостоятельный операнд формулы. Старые условия без кавычек трактовали
 *  числовой/логический текст как скаляр: сохраняем это, не принимая
 *  произвольный текст за код. */
const formulaOperand = (value: unknown): string => {
    if (typeof value !== 'string') return String(value);
    const scalar = value.trim();
    if (/^(?:true|false|null|undefined|NaN|[+-]?Infinity)$/.test(scalar)) return scalar;
    if (scalar && !Number.isNaN(Number(scalar))) return String(Number(scalar));
    return formulaStringLiteral(value);
};

/** `{ value }` — данные (литерал или экранированное содержимое строки),
 *  `{ text }` — то же, но вне кавычек всегда строковый литерал (числовой текст не становится числом),
 *  `{ raw }` — текст как есть, `undefined` — оставить токен. */
export type FormulaTokenValue = { value: unknown } | { text: string } | { raw: string } | undefined;

interface ICompiledToken {
    scanner: RegExp;
    inner: RegExp;
    probe: RegExp;
    groupCount: number;
}

// Токены — литералы кода и имена переменных конфигурации, их набор конечен.
const compiledTokens = new Map<string, ICompiledToken>();

/** Регулярки токена компилируются один раз: подстановка идёт на каждое вычисление формулы. */
const compileToken = (tokenSource: string): ICompiledToken => {
    let compiled = compiledTokens.get(tokenSource);
    if (!compiled) {
        compiled = {
            scanner: new RegExp(`'(?:\\\\.|[^'\\\\])*'|"(?:\\\\.|[^"\\\\])*"|${tokenSource}`, 'g'),
            inner: new RegExp(tokenSource, 'g'),
            probe: new RegExp(tokenSource),
            groupCount: new RegExp(`${tokenSource}|`).exec('')!.length - 1,
        };
        compiledTokens.set(tokenSource, compiled);
    }
    return compiled;
};

/** Подстановка данных в формулу по токенам `token` (RegExp без флагов). Токен бывает
 *  самостоятельным операндом и частью строкового шаблона ('{value}', "префикс {value}"):
 *  внутри кавычек подставляем только экранированное содержимое, снаружи — целый
 *  типизированный литерал. `resolve` получает токен, его группы и кавычку строки,
 *  внутри которой стоит токен (`undefined` — вне строки). */
export const substituteFormulaData = (
    expression: string,
    token: RegExp,
    resolve: (match: string, groups: Array<string | undefined>, quote?: string) => FormulaTokenValue,
): string => {
    const { scanner, inner, probe, groupCount } = compileToken(token.source);
    const resolveArgs = (match: string, args: unknown[], quote?: string) =>
        resolve(match, args.slice(0, groupCount) as Array<string | undefined>, quote);
    return expression.replace(scanner, (match: string, ...args: unknown[]) => {
        if (match[0] !== "'" && match[0] !== '"') {
            const resolved = resolveArgs(match, args);
            if (resolved === undefined) return match;
            if ('raw' in resolved) return resolved.raw;
            return 'text' in resolved ? formulaStringLiteral(resolved.text) : formulaOperand(resolved.value);
        }
        if (!probe.test(match)) return match;
        // Init распознаёт {поле} и FUNCTION(...) даже внутри строк:
        // эти символы кодируем, чтобы данные не превратились в новую формулу.
        return match.replace(inner, (innerMatch: string, ...innerArgs: unknown[]) => {
            const resolved = resolveArgs(innerMatch, innerArgs, match[0]);
            if (resolved === undefined) return innerMatch;
            if ('raw' in resolved) return resolved.raw;
            return stringContent('text' in resolved ? resolved.text : String(resolved.value), match[0]);
        });
    });
};

export const replaceFormulaValue = (expression: string, value: unknown): string =>
    substituteFormulaData(expression, /\{value\}/, () => ({ value }));
